import React, { useEffect, useState } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { collection, doc, getFirestore, onSnapshot, setDoc } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  ArrowLeft,
  Settings as SettingsIcon,
  X,
  Save,
  Edit,
  Info,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

function SettingsViewer({ onBack }) {
  const [internalSettings, setInternalSettings] = useState({});
  const [externalSettings, setExternalSettings] = useState({});
  const [settingsByCategory, setSettingsByCategory] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [editingCells, setEditingCells] = useState(new Set());
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    const db = getFirestore(app);
    const internalRef = collection(db, 'deviceSettings', 'everyFamilyVisible', 'settings');
    const externalRef = collection(db, 'deviceSettings', 'everyAccountAndEveryFamilyVisible', 'settings');

    const unsubInternal = onSnapshot(internalRef, (snapshot) => {
      const settings = {};
      snapshot.docs.forEach(d => { settings[d.id] = d.data(); });
      setInternalSettings(settings);
    }, (error) => {
      console.error('Error listening to internal settings:', error);
    });

    const unsubExternal = onSnapshot(externalRef, (snapshot) => {
      const settings = {};
      snapshot.docs.forEach(d => { settings[d.id] = d.data(); });
      setExternalSettings(settings);
    }, (error) => {
      console.error('Error listening to external settings:', error);
    });

    return () => {
      unsubInternal();
      unsubExternal();
    };
  }, []);

  useEffect(() => {
    const grouped = {};

    const addEntries = (settingsMap, scope) => {
      Object.entries(settingsMap).forEach(([category, fields]) => {
        Object.entries(fields).forEach(([key, meta]) => {
          if (meta && typeof meta === 'object' && 'value' in meta) {
            if (!grouped[category]) grouped[category] = [];
            grouped[category].push({
              id: `${scope}.${category}.${key}`,
              scope,
              category,
              key,
              meta,
              order: meta.order ?? 999,
              label: meta.label || key,
              description: meta.description || '',
              tooltip: meta.tooltip || '',
              type: meta.type,
              choices: meta.choices,
              value: meta.value
            });
          }
        });
      });
    };

    addEntries(internalSettings, 'everyFamilyVisible');
    addEntries(externalSettings, 'everyAccountAndEveryFamilyVisible');

    for (const category of Object.keys(grouped)) {
      grouped[category].sort((a, b) => a.order - b.order);
    }

    setSettingsByCategory(grouped);
    setCollapsedCategories(prev => {
      if (prev.size > 0) return prev;
      return new Set(Object.keys(grouped));
    });
  }, [internalSettings, externalSettings]);

  const formatValue = (value) => {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'object' && value?.toDate) return value.toDate().toLocaleString();
    return String(value);
  };

  const startEditing = (cellKey, entry) => {
    setEditingCells(prev => new Set(prev).add(cellKey));
    setEditValues(prev => ({
      ...prev,
      [cellKey]: entry.value
    }));
  };

  const cancelEditing = (cellKey) => {
    setEditingCells(prev => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
    setEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[cellKey];
      return newValues;
    });
  };

  const saveValue = async (cellKey, entry) => {
    const newValue = editValues[cellKey];

    try {
      const db = getFirestore(app);
      const settingsRef = doc(db, 'deviceSettings', entry.scope, 'settings', entry.category);
      await setDoc(settingsRef, { [entry.key]: { value: newValue } }, { merge: true });
      cancelEditing(cellKey);
    } catch (error) {
      console.error('Error saving setting:', error);
      alert('Failed to save setting. Please try again.');
    }
  };

  const isNumericType = (type) => ['int', 'float', 'number'].includes(type);

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const renderEditWidget = (cellKey, entry) => {
    const currentEditValue = editValues[cellKey];

    if (entry.choices && entry.choices.length > 0) {
      return (
        <Select
          value={String(currentEditValue)}
          onValueChange={(val) => {
            let parsed = val;
            if (val === 'true') parsed = true;
            else if (val === 'false') parsed = false;
            else if (!isNaN(val) && val !== '') parsed = Number(val);
            setEditValues(prev => ({ ...prev, [cellKey]: parsed }));
          }}
        >
          <SelectTrigger className="h-7 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {entry.choices.map((choice, i) => (
              <SelectItem key={i} value={String(choice.value)}>{choice.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (isNumericType(entry.type)) {
      return (
        <Input
          type="number"
          value={currentEditValue ?? ''}
          onChange={(e) => {
            const val = e.target.value === '' ? '' : (entry.type === 'int' ? parseInt(e.target.value) : parseFloat(e.target.value));
            setEditValues(prev => ({ ...prev, [cellKey]: val }));
          }}
          className="h-7 text-xs flex-1 text-center"
        />
      );
    }

    if (entry.type === 'bool') {
      return (
        <input
          type="checkbox"
          checked={!!currentEditValue}
          onChange={(e) => setEditValues(prev => ({ ...prev, [cellKey]: e.target.checked }))}
          className="h-4 w-4 accent-primary"
        />
      );
    }

    return (
      <Input
        value={currentEditValue ?? ''}
        onChange={(e) => setEditValues(prev => ({ ...prev, [cellKey]: e.target.value }))}
        className="h-7 text-xs flex-1"
        autoFocus
      />
    );
  };

  const categories = Object.keys(settingsByCategory).sort();

  return (
    <div className="mx-auto p-6 min-h-screen flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Device Management
        </Button>
        <SettingsIcon className="w-8 h-8" />
        <h1 className="text-3xl font-bold">Settings Viewer</h1>
      </div>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <SettingsIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No settings available</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {categories.map(category => {
            const entries = settingsByCategory[category];
            const isCollapsed = collapsedCategories.has(category);

            return (
              <Card key={category}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/30 transition-colors"
                >
                  {isCollapsed
                    ? <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  }
                  <span className="text-lg font-semibold capitalize">{category}</span>
                  <span className="text-sm text-muted-foreground">({entries.length})</span>
                </button>

                {!isCollapsed && (
                  <CardContent className="pt-0 pb-2 px-2">
                    <table className="w-full">
                      <tbody>
                        {entries.map((entry, index) => {
                          const cellKey = `global_${entry.id}`;
                          const isEditing = editingCells.has(cellKey);

                          return (
                            <tr key={entry.id} className={index % 2 === 0 ? 'bg-muted/60' : ''}>
                              <td className="py-1.5 px-2 text-sm font-mono whitespace-nowrap align-middle">
                                {entry.key}
                                {entry.scope === 'everyAccountAndEveryFamilyVisible' && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 ml-2 bg-blue-50 text-blue-600 border-blue-200">
                                    user controllable
                                  </Badge>
                                )}
                              </td>
                              <td className="py-1.5 px-2 align-middle">
                                <div className="flex items-center gap-1">
                                  {isEditing ? (
                                    <>
                                      <div className="min-w-[150px]">
                                        {renderEditWidget(cellKey, entry)}
                                      </div>
                                      <Button
                                        size="sm"
                                        onClick={() => saveValue(cellKey, entry)}
                                        className="h-7 w-7 p-0 flex-shrink-0"
                                      >
                                        <Save className="w-3 h-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => cancelEditing(cellKey)}
                                        className="h-7 w-7 p-0 flex-shrink-0"
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-sm font-mono">{formatValue(entry.value)}</span>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => startEditing(cellKey, entry)}
                                        className="h-6 w-6 p-1 opacity-60 hover:opacity-100 transition-opacity hover:bg-gray-100 border border-gray-300 bg-white/80 flex-shrink-0"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="py-1.5 px-2 text-xs text-muted-foreground align-middle">
                                <div className="flex items-center gap-1.5">
                                  <span>{entry.description}</span>
                                  {entry.tooltip && (
                                    <span className="relative group">
                                      <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 cursor-help" />
                                      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-normal min-w-[200px] max-w-md hidden group-hover:block z-20">
                                        {entry.tooltip}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SettingsViewer;
